import { ReactNode } from 'react';
import { CheckCircleFilled } from '@ant-design/icons';
import colorTokens from '../../../constants/design_system/colors';

type Props = {
  title: string;
  /** The chosen value's label; shown on the right when collapsed. */
  selectedLabel?: string | null;
  isOpen: boolean;
  onToggle: () => void;
  /** Fires on header press (mousedown), before any dropdown-dismiss collapses the
   * question. Lets the parent snapshot press-time state so the following click
   * toggles deterministically instead of racing an outside-click dismissal (#2621). */
  onHeaderMouseDown?: () => void;
  children: ReactNode;
  /** Extra classes appended to the card's outer wrapper (e.g. a fixed height). */
  className?: string;
};

/**
 * Collapsible question card (owner variant). A leading dot + the question title
 * make the header; collapsed + answered shows the selected value on the right;
 * open reveals the control. The parent owns open/closed so answering one
 * question can advance to the next.
 */
export default function AccordionQuestion({
  title,
  selectedLabel,
  isOpen,
  onToggle,
  onHeaderMouseDown,
  children,
  className,
}: Props) {
  const answered = selectedLabel != null && selectedLabel !== '';

  return (
    <div
      className={`flex flex-col gap-3 rounded-xl border border-solid border-neutral-300 p-5${className ? ` ${className}` : ''}`}
    >
      <div
        role="button"
        tabIndex={0}
        onMouseDown={onHeaderMouseDown}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') onToggle();
        }}
        className="flex items-center justify-between gap-3 cursor-pointer select-none"
      >
        <div className="flex items-center gap-2 text-sm font-medium text-brand-grey-dark">
          {/* Fixed-width marker slot so the title sits at the same x whether the
              question is answered (check) or not (dot) — otherwise it jumps 10px. */}
          <span className="flex h-4 w-4 shrink-0 items-center justify-center">
            {answered ? (
              <CheckCircleFilled style={{ fontSize: '16px' }} className="text-taskonioGreen-600" />
            ) : (
              <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: colorTokens.neutral[700] }} />
            )}
          </span>
          <span>{title}</span>
        </div>
        {answered && !isOpen && <span className="text-brand-grey-dark text-sm">{selectedLabel}</span>}
      </div>
      {isOpen && <div>{children}</div>}
    </div>
  );
}
